import React, {
  useMemo,
  useRef,
} from 'react';
import { ColDef, GridOptions } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useAgGridCommon } from '../../../common/table/agGrid/AgGridCommon';
import { common } from '../../../utils/agGrid/options';
import columnDefs, { BalanceDetailRow } from './columnDefs';
import get from 'lodash/get';
import {
  getCurrenexBalancesWithDetails,
} from 'reducers/accountsReducer';
import {
  getSelectedAccount,
} from 'reducers/accountHistoryReducer';
import { createLoadingSelector } from 'erisxkit/client';
import { BALANCES } from 'constants/actionTypes';
import { RootState } from 'ts/types/store';
import { useAppSelector } from 'hooks/useAppSelector';
import useCdfEnabled from 'hooks/useIsFeatureFlagEnabled/useCdfEnabled';
import {
  BANK,
  CUSTODIAN,
  WALLET,
  HOUSE,
} from 'constants/accountTypes';

export interface BalanceDetail {
  accountId: string;
  accountLabel: string;
  asset: string;
  openingBalance: string;
  spotMovement: string;
  exchangeFees: string;
  clearingFees: string;
  otherFees: string;
  assetMovement: string;
  realizedPAndL: string;
  futuresDelivery: string;
  variationMargin: string;
  fundingAmount: string;
  closingBalance: string;
  totalEquity: string;
  tmeBalance: string;
  purchasingPower: string;
  initialMargin: string;
  maintenanceMargin: string;
  pendingBalance: string;
  usdValue: string;
  totalExcessDeficit: string;
  settlementBalance: string;
  settlementDetail: string;
  settlementTime: string;
  currenexBalance: string;
  fd: string;
  symbol: string;
  unsettledNov: string;
  exercisedValue: string;
}

const gridOptions: GridOptions = {
  ...common,
  defaultColDef: { floatingFilter: false },
};

export const gridName = 'balance-details';

// Increment this version when adding/removing columns or making material changes to column definitions
// CO-933: Added new columns (unsettledNov, exercisedValue)
const BALANCE_DETAILS_GRID_VERSION = 1;

const BalanceDetailsGrid: React.FC = () => {
  const currenexAccountBalances: BalanceDetailRow[] = useAppSelector(getCurrenexBalancesWithDetails);
  const loading = useAppSelector((state: RootState) =>
    createLoadingSelector([BALANCES])(state),
  );
  const selectedAccount= useAppSelector(getSelectedAccount);
  const cdfEnabled = useCdfEnabled();
  const gridRef = useRef<AgGridReact>(null);
  const treeData = true;
  const treeDataChildrenField = '_subRows';

  const showCurrenexBalances = useMemo(() => {
    const categories = get(selectedAccount, 'categories', []);
    return !(
      categories.includes(BANK) ||
      categories.includes(CUSTODIAN) ||
      categories.includes(WALLET) ||
      categories.includes(HOUSE)
    );
  }, [selectedAccount]);

  const { gridVisible, initialGridSettings, onStateUpdated } = useAgGridCommon({
    gridName,
    version: BALANCE_DETAILS_GRID_VERSION,
  });

  const autoGroupColumnDef = useMemo<ColDef>(() => {
    return {
      headerName: 'Account Label',
      field:'accountLabel',
      minWidth: 280,
      cellRendererParams: {
        suppressCount: true,
      },
    };
  }, []);

  const columns: ColDef[] = useMemo(() => {
  return columnDefs(showCurrenexBalances, cdfEnabled);
  }, [showCurrenexBalances, cdfEnabled]);

  return (
    <div className="ag-theme-quartz">
      {gridVisible && (
        <AgGridReact
          ref={gridRef}
          rowData={currenexAccountBalances}
          columnDefs={columns}
          treeData={treeData}
          gridOptions={gridOptions}
          onStateUpdated={onStateUpdated}
          initialState={initialGridSettings}
          treeDataChildrenField={treeDataChildrenField}
          loading={loading}
          autoGroupColumnDef={autoGroupColumnDef}
        />
      )}
    </div>
  );
};

export default BalanceDetailsGrid;
